import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { UpgradePrompt } from '../Premium/UpgradePrompt';
import type { Role } from '../../types';
import { useTaskContext } from '../../contexts/TaskContext';

interface CustomRoleManagerProps {
  onClose: () => void;
}

export function CustomRoleManager({ onClose }: CustomRoleManagerProps) {
  const { hasFeature, showUpgradePrompt } = useSubscription();
  const { roles, updateRole } = useTaskContext();
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const isPremium = hasFeature('custom_roles');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const roleId = editingRole as Role;
    const name = formData.get('name') as string;
    const color = formData.get('color') as string;

    try {
      if (editingRole) {
        const { error } = await supabase
          .from('custom_roles')
          .upsert({
            user_id: user?.id,
            role: roleId,
            name,
            color
          }, {
            onConflict: 'user_id,role'
          });

        if (error) throw error;
        
        // Update local state
        setRoles(prev => prev.map(role =>
          role.id === roleId ? { ...role, name, color } : role
        ));

        onClose(); // Close modal after successful update
      }
    } catch (error) {
      console.error('Error saving role:', error);
      alert('Failed to update role. Please try again.');
    }
  };

  if (!isPremium) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">Customize Roles</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-black/20 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <UpgradePrompt
            feature="Role Customization"
            description="Customize the appearance and names of your task roles to match your workflow."
            onUpgrade={() => showUpgradePrompt('custom_roles')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Customize Roles</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/20 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {editingRole ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Role Name</label>
              <input
                type="text"
                name="name"
                defaultValue={roles.find(r => r.id === editingRole)?.name}
                className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Color</label>
              <input
                type="color"
                name="color"
                defaultValue={roles.find(r => r.id === editingRole)?.color}
                className="w-full h-10 rounded-xl"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="flex-1 bg-[#bbdde5] text-black rounded-xl py-2 font-medium hover:opacity-90 transition-opacity"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={() => setEditingRole(null)}
                className="flex-1 bg-black/20 rounded-xl py-2 hover:bg-black/30 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-2">
            {roles.map((role) => (
              <div
                key={role.id}
                className="flex items-center justify-between p-3 bg-black/20 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-lg"
                    style={{ backgroundColor: role.color }}
                  />
                  <span>{role.name}</span>
                </div>
                <button
                  onClick={() => setEditingRole(role.id)}
                  className="px-3 py-1 bg-black/20 rounded-lg text-sm hover:bg-black/30 transition-colors"
                >
                  Customize
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}